import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import { defaultQueryOptions } from '@/config/react-query.config';

interface UpdatePaymentLinkResponse {
  /** Absent when the club has no hosted billing page configured. */
  updatePaymentLink?: string;
}

/**
 * The member's club's hosted billing page, for the past-due card
 * update screen.
 *
 * Fetched on demand rather than carried on the cached location: it is
 * read live from GHL on the API side and only this one screen wants
 * it, so putting it on `my-location` would have meant a CRM round
 * trip on every app boot for a value almost no session uses.
 *
 * An absent link is a normal answer, not an error — it means the club
 * has not been migrated and the in-app Stripe form still applies. The
 * API swallows GHL failures into the same shape for the same reason,
 * so a member is never left with no way to pay.
 */
export const useUpdatePaymentLink = (enabled = true) => {
  return useQuery<UpdatePaymentLinkResponse, Error>({
    queryKey: ['member-location', 'update-payment-link'],
    queryFn: async () => {
      const { data } = await api.get<UpdatePaymentLinkResponse>(
        '/member-locations/my-location/update-payment-link',
      );
      return data;
    },
    enabled,
    ...defaultQueryOptions,
  });
};

export default useUpdatePaymentLink;
